import React from 'react';
import { Box, Container, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import '../glow-effect.css';

const ServicesCircles = ({ items, onItemClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const services = items || [
    {
      key: 'consultory',
      titleKey: 'services.consultory.title',
      descriptionKey: 'services.consultory.description',
    },
    {
      key: 'development',
      titleKey: 'services.development.title',
      descriptionKey: 'services.development.description',
    },
    {
      key: 'seo',
      titleKey: 'services.seo.title',
      descriptionKey: 'services.seo.description',
    },
  ];

  const CardBox = styled(Box)(({ theme, index }) => ({
    position: 'relative',
    width: '320px',
    height: '480px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '40px 30px',
    transition: '0.5s',
    cursor: 'pointer',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '50px',
      width: '50%',
      height: '100%',
      textDecoration: 'none',
      background: index === 0 
        ? 'linear-gradient(315deg, #ffbc00, #ff0058)'
        : index === 1
        ? 'linear-gradient(315deg, #03a9f4, #ff0058)'
        : 'linear-gradient(315deg, #4dff03, #00d0ff)',
      borderRadius: '8px',
      transform: 'skewX(15deg)',
      transition: '0.5s',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '50px',
      width: '50%',
      height: '100%',
      background: index === 0 
        ? 'linear-gradient(315deg, #ffbc00, #ff0058)'
        : index === 1
        ? 'linear-gradient(315deg, #03a9f4, #ff0058)'
        : 'linear-gradient(315deg, #4dff03, #00d0ff)',
      borderRadius: '8px',
      transform: 'skewX(15deg)',
      transition: '0.5s',
      filter: 'blur(30px)',
    },
    '&:hover::before, &:hover::after': {
      transform: 'skewX(0deg)',
      left: '20px',
      width: 'calc(100% - 90px)',
    },
    '&:hover > div:last-child': {
      left: '-25px',
      padding: '60px 40px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '280px',
      height: '420px',
      margin: '20px 15px',
      '&:hover > div:last-child': {
        left: '-15px',
        padding: '40px 30px',
      },
    },
  }));

  const CardSpan = styled(Box)({
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 5,
    pointerEvents: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: 0,
      height: 0,
      borderRadius: '8px',
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      opacity: 0,
      transition: '0.1s',
      animation: 'cardAnimate 2s ease-in-out infinite',
      boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: '100%',
      height: '100%',
      borderRadius: '8px',
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      opacity: 0,
      transition: '0.5s',
      animation: 'cardAnimate 2s ease-in-out infinite',
      boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
      animationDelay: '-1s',
    },
    '&:hover::before': {
      top: '-50px',
      left: '50px',
      width: '100px',
      height: '100px',
      opacity: 1,
    },
    '&:hover::after': {
      bottom: '-50px',
      right: '50px',
      width: '100px',
      height: '100px',
      opacity: 1,
    },
  });

  const CardContent = styled(Box)(({ theme }) => ({
    position: 'relative',
    left: 0,
    padding: '20px 40px',
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    zIndex: 1,
    transition: '0.5s',
    color: '#fff',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '15px 30px',
    },
  }));

  const BlackCircle = styled(Box)(({ theme }) => ({
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    backgroundColor: '#000000',
    border: '2px solid #ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
    transition: 'transform 0.3s ease',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      width: '150px',
      height: '150px',
    },
  }));

  const CircleText = styled(Typography)(({ theme }) => ({
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontFamily: '"Montserrat", sans-serif',
    padding: theme.spacing(1, 2),
    lineHeight: 1.3,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
      padding: theme.spacing(0.5, 1.5),
    },
  }));

  const DescriptionText = styled(Typography)(({ theme }) => ({
    color: '#ffffff',
    fontSize: '0.95rem',
    textAlign: 'center',
    lineHeight: 1.6,
    margin: 0,
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.85rem',
    },
  }));

  return (
    <>
      <style>
        {`
          @keyframes cardAnimate {
            0%, 100% {
              transform: translateY(10px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
        `}
      </style>
      <Container
        sx={{
          py: 8,
          px: 2,
          backgroundColor: 'transparent',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {services.map((service, index) => (
          <AnimatedSection direction="bottom" delay={index * 0.2} key={service.key}>
            <CardBox
              index={index}
              onClick={() => {
                if (onItemClick) {
                  onItemClick(service);
                  return;
                }
                if (service.key === 'seo') {
                  navigate('/geo-para-chatgpt-gemini');
                } else {
                  navigate('/coming-soon', { state: { serviceKey: service.key } });
                }
              }}
            >
              <CardSpan />
              <CardContent>
                <BlackCircle>
                  <CircleText>{t(service.titleKey)}</CircleText>
                </BlackCircle>
                <DescriptionText>{t(service.descriptionKey)}</DescriptionText>
              </CardContent>
            </CardBox>
          </AnimatedSection>
        ))}
      </Container>
    </>
  );
};

export default ServicesCircles;

